import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import React, { useEffect, useState } from "react";
import { useGetApi } from "../../../hooks/useGetApi";
import { getCategoryApi } from "../../../services/category.service";
import { getItemApi } from "../../../services/item.service";
import { getUserApi } from "../../../services/user.service";
import dayjs from "dayjs";
import { addSurgeryProcedureByVisitIdApi } from "../../../services/consultation.service";
import { toast } from "react-toastify";

// Grid Item Component to encapsulate useGetApi
const GridItem = ({
  index,
  item,
  handleChange,
  handleRemoveItem,
  categoryList,
  doctorsList,
}) => {
  const { dataList: itemList } = useGetApi({
    apiFunction: item.category ? getItemApi : null,
    params: {
      category: item.category,
      limit: Number.MAX_SAFE_INTEGER,
    },
    dataKey: "items",
    dependencies: [item.category],
  });

  return (
    <Grid
      container
      spacing={3}
      sx={{
        mb: index !== 0 ? "40px" : "20px",
      }}
    >
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Autocomplete
          options={
            categoryList.filter(
              (category) =>
                category.categoryName === "Surgical" ||
                category.categoryName === "General Procedure"
            ) || []
          }
          getOptionLabel={(option) => `${option.categoryName} (${option.code})`}
          value={
            categoryList.find((category) => category._id === item.category) ||
            null
          }
          onChange={(_, newValue) =>
            handleChange(
              index,
              "category",
              newValue ? newValue._id : null,
              itemList
            )
          }
          renderInput={(params) => (
            <TextField {...params} label="Category" required fullWidth />
          )}
          sx={{ background: "white" }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Autocomplete
          options={itemList || []}
          getOptionLabel={(option) => `${option.itemName} (${option.itemType})`}
          value={
            itemList.find((singleItem) => singleItem._id === item.item) || null
          }
          onChange={(_, newValue) =>
            handleChange(
              index,
              "item",
              newValue ? newValue._id : null,
              itemList
            )
          }
          renderInput={(params) => (
            <TextField {...params} label="Item Name" required fullWidth />
          )}
          sx={{ background: "white" }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Autocomplete
          options={doctorsList || []}
          getOptionLabel={(option) =>
            `${option.firstName} ${option.lastName} (${option.userId})`
          }
          value={
            doctorsList.find((doctor) => doctor._id === item.doctor) || null
          }
          onChange={(_, newValue) =>
            handleChange(
              index,
              "doctor",
              newValue ? newValue._id : null,
              itemList
            )
          }
          renderInput={(params) => (
            <TextField {...params} label="Doctor Name" fullWidth />
          )}
          sx={{ background: "white" }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <TextField
          label="Normal Rate (Rs)"
          name="amount"
          type="number"
          value={item.amount}
          onChange={(e) =>
            handleChange(index, e.target.name, e.target.value, itemList)
          }
          required
          fullWidth
          sx={{ background: "white" }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <DatePicker
          label="Date"
          value={item.date ? dayjs(item.date) : null}
          onChange={(newDate) => {
            handleChange(index, "date", newDate?.toISOString(), itemList);
          }}
          slotProps={{ textField: { fullWidth: true, required: true } }}
          sx={{ background: "white" }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <TextField
          label="Add Note"
          name="doctorNotes"
          value={item.doctorNotes}
          onChange={(e) =>
            handleChange(index, e.target.name, e.target.value, itemList)
          }
          fullWidth
          sx={{ background: "white" }}
        />
      </Grid>

      {index !== 0 && (
        <Box sx={{ textAlign: "right", width: "100%" }}>
          <Button
            variant="text"
            onClick={() => handleRemoveItem(index)}
            color="error"
            sx={{ ml: "25px", mt: "5px", textTransform: "none" }}
          >
            Remove
          </Button>
        </Box>
      )}
    </Grid>
  );
};

const AddSurgeryModal = ({ open, onClose, id }) => {
  const initialState = {
    category: null,
    item: null,
    doctor: null,
    date: dayjs(),
    doctorNotes: "",
    amount: 0,
  };
  const [gridItems, setGridItems] = useState([initialState]);
  const [loading, setLoading] = useState(false);

  const { dataList: categoryList } = useGetApi({
    apiFunction: getCategoryApi,
    params: {
      limit: Number.MAX_SAFE_INTEGER,
    },
    dataKey: "categories",
  });

  const { dataList: doctorsList } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "users",
  });

  const handleChange = (index, name, value, itemList) => {
    setGridItems((prevItems) =>
      prevItems.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [name]: value };

          // Reset item and amount when category changes
          if (name === "category") {
            updatedItem.item = null;
            updatedItem.amount = 0;
          }

          // Update amount when item changes
          if (name === "item" && value && itemList) {
            const selectedItem = itemList.find(
              (singleItem) => singleItem._id === value
            );
            updatedItem.amount = selectedItem?.normalRate || 0;
          }

          return updatedItem;
        }
        return item;
      })
    );
  };

  const handleAddItem = () => {
    setGridItems((prevItems) => [...prevItems, { ...initialState }]);
  };

  const handleRemoveItem = (index) => {
    setGridItems((prevItems) => prevItems.filter((_, i) => i !== index));
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (id) {
      let response;

      setLoading(true);

      response = await addSurgeryProcedureByVisitIdApi(id, gridItems);

      setLoading(false);

      if (response?.code === 200 || response?.code === 201) {
        toast.success(`Surgery Procedure added successfully`);
        setGridItems([initialState]);
        onClose();
      } else {
        toast.error(response?.message || "Some error occurred.");
      }
    } else {
      toast.error("Visit ID is required.");
    }
  };

  useEffect(() => {
    setGridItems([initialState]);
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          minWidth: "95vw", // Increased modal width
          maxWidth: "98vw", // Prevent overflow
          bgcolor: "#E5F4FF",
        },
      }}
    >
      <DialogContent>
        <Box component={"form"} onSubmit={submitHandler}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: "20px",
            }}
          >
            <Typography variant="h6" sx={{ color: "#5D6A82" }}>
              Add Surgery/Procedure
            </Typography>
            <Close
              onClick={onClose}
              sx={{ cursor: "pointer", color: "#5D6A82" }}
            />
          </Box>

          {gridItems?.map((item, index) => (
            <GridItem
              key={index}
              index={index}
              item={item}
              handleChange={handleChange}
              handleRemoveItem={handleRemoveItem}
              categoryList={categoryList}
              doctorsList={doctorsList}
            />
          ))}

          <Button
            onClick={handleAddItem}
            variant="text"
            sx={{ color: "#92A6CC", textTransform: "none" }}
          >
            + Add Surgery Procedure
          </Button>

          <Box sx={{ my: "20px", textAlign: "center" }}>
            <Button
              variant="contained"
              sx={{ width: "150px", textTransform: "none" }}
              type="submit"
              disabled={loading}
            >
              {loading ? <CircularProgress size={"20px"} /> : "Submit"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddSurgeryModal;
